/**
 * armor.ts — the equipment catalogue: what you can wear, where it goes, and
 * what it does.
 *
 * This is shared, not client art, for one reason: what you are wearing is part
 * of the world, so the SERVER owns it. A client asks for a loadout, the server
 * sanitizes it and puts it in the snapshot, and every client draws the same
 * thing. A purely local wardrobe would let two players see different opponents,
 * and now that kit changes the numbers it would let them compute different
 * damage too.
 *
 * ## Slots and pieces
 *
 * A `Loadout` is one piece per slot, or nothing. Pieces are the catalogue; the
 * loadout is the choice. Nothing anywhere may assume a slot is filled.
 *
 * ## Tags are not stats
 *
 * `light`/`medium`/`heavy` are TAGS. They exist so the wardrobe can offer three
 * coherent looks and a one-click set, and so a piece can say what family it
 * belongs to. They are deliberately NOT where the numbers come from — every
 * piece carries its own `stats`, so a mail coif and a mail hauberk can weigh
 * what they should weigh rather than what their family average says.
 *
 * Add a stat by adding a field to `PieceStats`, giving every piece a value, and
 * writing the aggregate that reads it. That is the whole extension path, and it
 * is why the numbers live on the piece.
 *
 * ## What armour may and may not touch
 *
 * It may change damage taken and how fast you walk. It may NOT change a
 * hurtbox, a reach, a clip window or a hitbox of any kind: the body capsule and
 * the sword capsule are what the server sweeps, and armour is drawn over the
 * top of them (AGENTS.md: all collision goes through the engine). Kit that
 * quietly grew the shapes the server tests against would make the picture and
 * the collider disagree, which is the one failure the whole render side is
 * arranged to prevent.
 *
 * The MESHES live in the client's `kit.ts`. What is shared is the vocabulary:
 * slots, piece ids, stats and palettes, so both ends mean the same thing by
 * "hauberk".
 */
import { FEEL } from './feel.js';
/** Where a piece is worn. The renderer has one builder per piece, not per slot. */
export const EQUIP_SLOTS = [
    'helm',
    'shoulders',
    'chest',
    'gauntlets',
    'legs',
    'boots',
    'weapon',
];
/**
 * The three families a piece can belong to. A TAG, not a stat — see the module
 * comment. What it buys is the wardrobe's "wear the whole light set" button and
 * a consistent palette per family.
 */
export const WEIGHT_CLASSES = ['light', 'medium', 'heavy'];
/**
 * Every piece in the game, in wardrobe order: by slot, then light → heavy.
 *
 * Ids are unique across all slots and are what travels on the wire, so renaming
 * one is a wire change. `sanitizeLoadout` refuses a piece worn in the wrong
 * slot, so an id is bound to its slot as firmly as to its mesh.
 */
export const PIECE_IDS = [
    'hood',
    'soldiersHelm',
    'armet',
    'spaulders',
    'mailVoiders',
    'pauldrons',
    'cloak',
    'hauberk',
    'cuirass',
    'breeches',
    'chausses',
    'cuisses',
    'turnshoes',
    'mailShoes',
    'sabatons',
    'handwraps',
    'mailMitts',
    'plateGauntlets',
    'chrysamere',
];
/** Cloth and leather: waxed browns, wool, brass buckles. */
const LIGHT_PALETTE = { steel: 0x6a5540, trim: 0x8f7a44, cloth: 0x3f3a33 };
/** Mail: dark rings with an oily sheen, over leather. */
const MEDIUM_PALETTE = { steel: 0x6a7480, trim: 0x453c33, cloth: 0x2a2c32 };
/**
 * Plate: cool grey steel with gilt edging over dark padding.
 *
 * Notably darker than it looks like it should be on paper. Metal takes almost
 * all of its brightness from what it REFLECTS rather than from its base colour,
 * so once the scene gained an environment map a light grey read as chrome.
 */
/**
 * Mid steel-grey. Too dark and limbs read as black cloth under dungeon light;
 * too bright and env-map reflections turn every cylinder into chrome.
 */
const HEAVY_PALETTE = { steel: 0x8a929c, trim: 0x8a6e2a, cloth: 0x1c1610 };
const PALETTES = {
    light: LIGHT_PALETTE,
    medium: MEDIUM_PALETTE,
    heavy: HEAVY_PALETTE,
};
export const PIECES = {
    // ---- helm ----
    hood: {
        id: 'hood',
        slot: 'helm',
        weightClass: 'light',
        name: 'Wool Hood',
        blurb: 'A cowl and a mantle. Keeps the rain off.',
        stats: { weight: 0.4, armor: 0.06 },
        palette: LIGHT_PALETTE,
    },
    soldiersHelm: {
        id: 'soldiersHelm',
        slot: 'helm',
        weightClass: 'medium',
        name: "Soldier's Helm",
        blurb: 'Domed steel, long cheek plates, face open.',
        stats: { weight: 2.4, armor: 0.17 },
        palette: MEDIUM_PALETTE,
    },
    armet: {
        id: 'armet',
        slot: 'helm',
        weightClass: 'heavy',
        // Catalogue id stays `armet` for the packed loadout codec; the piece itself
        // is a late-14th / early-15th hounskull bascinet.
        name: 'Hounskull',
        blurb: 'Pointed bascinet with a dog-faced visor, c. 1380–1420.',
        stats: { weight: 3.4, armor: 0.3 },
        palette: HEAVY_PALETTE,
    },
    // ---- shoulders ----
    spaulders: {
        id: 'spaulders',
        slot: 'shoulders',
        weightClass: 'light',
        name: 'Leather Spaulders',
        blurb: 'Boiled leather caps, buckled at the arm.',
        stats: { weight: 0.7, armor: 0.06 },
        palette: LIGHT_PALETTE,
    },
    mailVoiders: {
        id: 'mailVoiders',
        slot: 'shoulders',
        weightClass: 'medium',
        name: 'Mail Voiders',
        blurb: 'Rings over the joint the sleeve leaves open.',
        stats: { weight: 2, armor: 0.15 },
        palette: MEDIUM_PALETTE,
    },
    pauldrons: {
        id: 'pauldrons',
        slot: 'shoulders',
        weightClass: 'heavy',
        name: 'Pauldrons',
        blurb: 'Articulated lames over a domed cap.',
        stats: { weight: 2.8, armor: 0.28 },
        palette: HEAVY_PALETTE,
    },
    // ---- chest ----
    cloak: {
        id: 'cloak',
        slot: 'chest',
        weightClass: 'light',
        name: 'Travelling Cloak',
        blurb: 'A quilted gambeson under a long wool cloak.',
        stats: { weight: 2.4, armor: 0.07 },
        palette: LIGHT_PALETTE,
    },
    hauberk: {
        id: 'hauberk',
        slot: 'chest',
        weightClass: 'medium',
        name: 'Mail Hauberk',
        blurb: 'Elbow sleeves, skirt to the thigh. Heavy on the shoulders.',
        stats: { weight: 9.5, armor: 0.2 },
        palette: MEDIUM_PALETTE,
    },
    cuirass: {
        id: 'cuirass',
        slot: 'chest',
        weightClass: 'heavy',
        name: 'Plate Cuirass',
        blurb: 'Breast, back, fauld and pauldrons, all articulated.',
        stats: { weight: 12, armor: 0.34 },
        palette: HEAVY_PALETTE,
    },
    // ---- legs ----
    breeches: {
        id: 'breeches',
        slot: 'legs',
        weightClass: 'light',
        name: 'Cross-Gartered Breeches',
        blurb: 'Wool, wrapped and tied from ankle to knee.',
        stats: { weight: 0.9, armor: 0.05 },
        palette: LIGHT_PALETTE,
    },
    chausses: {
        id: 'chausses',
        slot: 'legs',
        weightClass: 'medium',
        name: 'Mail Chausses',
        blurb: 'Mail hose laced to a belt. Loud when you run.',
        stats: { weight: 6, armor: 0.17 },
        palette: MEDIUM_PALETTE,
    },
    cuisses: {
        id: 'cuisses',
        slot: 'legs',
        weightClass: 'heavy',
        name: 'Cuisses & Greaves',
        blurb: 'Thigh plates, winged poleyns, shins enclosed.',
        stats: { weight: 7.2, armor: 0.28 },
        palette: HEAVY_PALETTE,
    },
    // ---- boots ----
    turnshoes: {
        id: 'turnshoes',
        slot: 'boots',
        weightClass: 'light',
        name: 'Leather Turnshoes',
        blurb: 'Soft soles. You will feel every stone.',
        stats: { weight: 0.6, armor: 0.04 },
        palette: LIGHT_PALETTE,
    },
    mailShoes: {
        id: 'mailShoes',
        slot: 'boots',
        weightClass: 'medium',
        name: 'Mail Shoes',
        blurb: 'Riveted rings over the instep, leather beneath.',
        stats: { weight: 1.6, armor: 0.13 },
        palette: MEDIUM_PALETTE,
    },
    sabatons: {
        id: 'sabatons',
        slot: 'boots',
        weightClass: 'heavy',
        name: 'Sabatons',
        blurb: 'Overlapping lames to a blunt point.',
        stats: { weight: 2.4, armor: 0.24 },
        palette: HEAVY_PALETTE,
    },
    // ---- gauntlets ----
    handwraps: {
        id: 'handwraps',
        slot: 'gauntlets',
        weightClass: 'light',
        name: 'Leather Handwraps',
        blurb: 'Strapping to the wrist. Grip, mostly.',
        stats: { weight: 0.3, armor: 0.05 },
        palette: LIGHT_PALETTE,
    },
    mailMitts: {
        id: 'mailMitts',
        slot: 'gauntlets',
        weightClass: 'medium',
        name: 'Mail Mitts',
        blurb: 'Mail backs, bare leather palms so you can still hold a grip.',
        stats: { weight: 1, armor: 0.14 },
        palette: MEDIUM_PALETTE,
    },
    plateGauntlets: {
        id: 'plateGauntlets',
        slot: 'gauntlets',
        weightClass: 'heavy',
        name: 'Plate Gauntlets',
        blurb: 'Flared cuffs, knuckle lames, scaled fingers.',
        stats: { weight: 1.6, armor: 0.26 },
        palette: HEAVY_PALETTE,
    },
    // ---- weapon ----
    /**
     * The only weapon there is, and everyone starts with it.
     *
     * It sits in the catalogue like everything else rather than being special-
     * cased, so the day a second sword arrives it is one entry and one mesh
     * rather than a new system. Its `armor` is zero — you cannot wear a sword —
     * but its `weight` is real and counts against your footwork like any other
     * kit, which is the whole reason the weapon slot is not inert.
     */
    chrysamere: {
        id: 'chrysamere',
        slot: 'weapon',
        weightClass: 'medium',
        name: 'Chrysamere',
        blurb: "The Paladin's Blade. A broad gold leaf, two-handed.",
        stats: { weight: 2.5, armor: 0 },
        palette: { steel: 0xc9a13f, trim: 0x8a6a24, cloth: 0x7e2119 },
    },
};
/**
 * Where a hit landed.
 *
 * This lives here rather than in `combat.ts` because it is now first and
 * foremost an ARMOUR question — which piece was struck — and `combat.ts`
 * imports it back for the height test that decides which one it was. `arm` is
 * not a height zone: it means the sword-arm hurtbox was hit rather than the
 * body capsule.
 */
export const HIT_ZONES = ['head', 'torso', 'legs', 'feet', 'arm'];
/**
 * Which slots protect a zone.
 *
 * A list, because more than one piece can plausibly be in the way of the same
 * hit — the sword arm is covered by gauntlets at the wrist and by a shoulder
 * piece at the top, and both are armour on the arm. `armorAt` takes the BEST of
 * them, never the sum: see there for why.
 *
 * The arm hurtbox runs elbow-to-hand, so gauntlets are the piece most literally
 * in the way; shoulders are listed with them because the slot would otherwise
 * be pure decoration, and a slot that changes nothing is a slot nobody thinks
 * about.
 */
export const ZONE_SLOTS = {
    head: ['helm'],
    torso: ['chest'],
    legs: ['legs'],
    feet: ['boots'],
    arm: ['gauntlets', 'shoulders'],
};
/**
 * Bare in every slot. Not the default — see `defaultLoadout`.
 *
 * Built FROM `EQUIP_SLOTS` rather than written out, so adding a slot cannot
 * leave a hole here that only shows up as an `undefined` somewhere downstream.
 */
export const emptyLoadout = () => Object.fromEntries(EQUIP_SLOTS.map((slot) => [slot, null]));
/**
 * What a player spawns in: unarmoured, holding the sword.
 *
 * The weapon is filled because a duellist without a blade is not a state the
 * game has anything to say about; the armour is not, because starting bare is
 * what makes the first trip to the wardrobe mean something.
 */
export const defaultLoadout = () => ({ ...emptyLoadout(), weapon: 'chrysamere' });
/** Every piece that goes in a slot, in catalogue order. */
export const piecesForSlot = (slot) => PIECE_IDS.map((id) => PIECES[id]).filter((piece) => piece.slot === slot);
/** The piece worn in a slot, or undefined if that slot is bare. */
export function equipped(loadout, slot) {
    const id = loadout[slot];
    return id === null ? undefined : PIECES[id];
}
/**
 * The whole family worn at once, plus the sword: what a set button equips.
 *
 * Built by filtering the catalogue rather than listed by hand, so a new piece
 * joins its family's set the moment it exists and cannot be forgotten here.
 */
export function suitOf(weightClass) {
    const loadout = emptyLoadout();
    for (const id of PIECE_IDS) {
        const piece = PIECES[id];
        if (piece.slot === 'weapon' || piece.weightClass !== weightClass)
            continue;
        loadout[piece.slot] = id;
    }
    loadout.weapon = 'chrysamere';
    return loadout;
}
/** The palette a family is drawn in, for wardrobe swatches. */
export const paletteFor = (weightClass) => PALETTES[weightClass];
// ---------------------------------------------------------------------------
// aggregates — the only things allowed to interpret PieceStats
// ---------------------------------------------------------------------------
const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
/** Total mass carried, kg. Everything counts, including the sword. */
export function loadoutWeight(loadout) {
    let total = 0;
    for (const slot of EQUIP_SLOTS)
        total += equipped(loadout, slot)?.stats.weight ?? 0;
    return total;
}
/**
 * Fraction of damage absorbed by what covers `zone`, 0..1.
 *
 * The BEST single piece, never a sum. Layering plate over mail over gambeson is
 * a system this game does not have, and adding slots together would make a full
 * heavy kit absorb everything wherever it was struck — which would delete the
 * aiming decision the zones exist to create. Taking the best also means a cheap
 * piece can never be stacked with another to reach protection neither of them
 * pays for.
 */
export function armorAt(loadout, zone) {
    let best = 0;
    for (const slot of ZONE_SLOTS[zone]) {
        best = Math.max(best, equipped(loadout, slot)?.stats.armor ?? 0);
    }
    return clamp(best, 0, 0.95);
}
/**
 * Move-speed multiplier for what you are carrying.
 *
 * Linear in total weight between a free allowance and a reference load, then
 * flat. Both ends compute this from the same loadout — the server in
 * `statusSystem`, the client in its predictor — because a second opinion about
 * how fast a man in plate walks is a permanent invisible mispredict
 * (AGENTS.md: the movement step lives in shared).
 *
 * Deliberately gentle. Plate should feel like a decision, not a punishment; the
 * damage it turns aside is the real trade, and the game is already slow enough
 * that a large penalty would read as broken rather than heavy.
 */
export function loadoutMoveMult(loadout) {
    const over = loadoutWeight(loadout) - FEEL.ENCUMBRANCE_FREE_KG;
    const span = Math.max(1e-6, FEEL.ENCUMBRANCE_REF_KG - FEEL.ENCUMBRANCE_FREE_KG);
    return 1 - clamp(over / span, 0, 1) * FEEL.ENCUMBRANCE_MAX_SLOW;
}
export function packLoadout(loadout) {
    return EQUIP_SLOTS.map((slot) => {
        const id = loadout[slot];
        return id === null ? 0 : PIECE_IDS.indexOf(id) + 1;
    });
}
/**
 * Reads a packed loadout back. Total: anything unrecognized, out of range, or
 * worn in the wrong slot becomes bare rather than throwing, because this runs
 * on untrusted bytes on the server and on a possibly-stale client after a
 * catalogue change.
 */
export function unpackLoadout(packed) {
    const loadout = emptyLoadout();
    if (!Array.isArray(packed))
        return loadout;
    EQUIP_SLOTS.forEach((slot, index) => {
        const code = packed[index];
        if (typeof code !== 'number' || !Number.isInteger(code) || code < 1)
            return;
        const id = PIECE_IDS[code - 1];
        if (id === undefined)
            return;
        // The slot is part of a piece's identity: a armet cannot be worn on
        // the feet however the packet asks.
        if (PIECES[id].slot !== slot)
            return;
        loadout[slot] = id;
    });
    return loadout;
}
/** Coerces an untrusted loadout object. Server-side gate on the equip message. */
export function sanitizeLoadout(raw) {
    const loadout = emptyLoadout();
    if (typeof raw !== 'object' || raw === null)
        return loadout;
    const source = raw;
    for (const slot of EQUIP_SLOTS) {
        const id = source[slot];
        if (typeof id !== 'string')
            continue;
        if (!PIECE_IDS.includes(id))
            continue;
        if (PIECES[id].slot !== slot)
            continue;
        loadout[slot] = id;
    }
    return loadout;
}
//# sourceMappingURL=armor.js.map